import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { selectVirtualCardIdFromRoute } from 'src/app/BillPayDetail/redux/billPayDetail.selectors'
import ActiveUntilBox from 'src/components/ActiveUntilBox/ActiveUntilBox'
import ActivityBox from 'src/components/ActivityBox/ActivityBox'
import AdditionalVirtualCardDetailsBox from 'src/components/AdditionalVirtualCardDetailsBox/AdditionalVirtualCardDetailsBox'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotesBox from 'src/components/NotesBox/NotesBox'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import PendingModificationBox from 'src/components/PendingModificationBox/PendingModificationBox'
import RecurringBox from 'src/components/RecurringBox/RecurringBox'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import VirtualCardAccountingBox from 'src/components/VirtualCardAccountingBox/VirtualCardAccountingBox'
import VirtualCardDetailHeader from 'src/components/VirtualCardDetailHeader/VirtualCardDetailHeader'
import VirtualCardDetailTopnav from 'src/components/VirtualCardDetailTopnav/VirtualCardDetailTopnav'
import { useTranslate } from 'src/localization/Trns'
import { TransactionStatus } from 'src/models/transaction.models'
import { ApprovalStatus, ScreeningStatus } from 'src/models/virtualCard.models'
import { connectedReplace, Rdrct } from 'src/navigation/Lnk'
import {
  billPayDetailUrl,
  screenVirtualCardUrl,
  updateVirtualCardUrl,
  userScreeningInfoUrl,
} from 'src/navigation/urls'
import { useQueryCreditCard } from 'src/queries/creditCard.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryOwnUser, useQueryUser } from 'src/queries/users.queries'
import {
  useQueryVirtualCardDetail,
  useQueryVirtualCardPermissions,
} from 'src/queries/virtualCards.queries'
import {
  getVirtualCardsStatisticsClearedTransactionCreditAmount,
  getVirtualCardsStatisticsClearedTransactionDebitAmount,
} from 'src/redux/actions/data/virtualCards.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectCardApprovalsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectVirtualCardClearedTransactionAmountFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BillPayDetail: React.FC = () => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const showBillPayHistory = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_bill_pay_history

  const virtualCardId = useSelector(selectVirtualCardIdFromRoute)
  const { data: virtualCardPermissions, isLoading: isPermissionsLoading } =
    useQueryVirtualCardPermissions({ virtualCardId })
  const { data: virtualCard, isLoading: isVirtualCardLoading } =
    useQueryVirtualCardDetail({ virtualCardId })
  const { data: sender, isLoading: isSenderLoading } = useQueryUser({
    userId: virtualCard?.cardholderId,
  })
  const { data: user } = useQueryOwnUser({})
  const { data: creditCard } = useQueryCreditCard({
    creditCardId: virtualCard?.creditCardId,
  })

  const userId = useSelector(selectUserIdFromState)
  const vcClearedTransactionAmountFromState = useSelector(
    selectVirtualCardClearedTransactionAmountFromState,
  )
  const appName = useSelector(selectAppName)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)

  const isPendingFinalApproval =
    virtualCard?.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval &&
    cardApprovalRules?.approverId !== undefined

  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const hasIntegrations = activeIntegration !== undefined

  const hideCardDetails =
    !user?.isInExplicitOrg && user?.organizationId !== sender?.organizationId

  const virtualCardTransactionFilters: TransactionsFiltersBody = {
    virtualCardId: [virtualCardId],
    status: [
      TransactionStatus.Cleared,
      TransactionStatus.Declined,
      TransactionStatus.Pending,
      TransactionStatus.AuthReversal,
    ],
    sort: '-dateAuthsFirst',
  }

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 76)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 76)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatch(
      getVirtualCardsStatisticsClearedTransactionDebitAmount([virtualCardId]),
    )
    dispatch(
      getVirtualCardsStatisticsClearedTransactionCreditAmount([virtualCardId]),
    )
  }, [virtualCard?.status, virtualCardId])

  useEffect((): void => {
    // refetch all visible transactions in case expense categories or vendor updated
    queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
  }, [virtualCard])

  useEffect(() => {
    if (virtualCard?.screeningRequired && virtualCard.isActive) {
      if (virtualCard.screening?.status === ScreeningStatus.NeedsMoreInfo) {
        dispatch(connectedReplace(screenVirtualCardUrl(virtualCard.id)))
      } else if (virtualCard.screening?.status === ScreeningStatus.Forbidden) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.Forbidden}`,
            state: {},
            hash: '',
          }),
        )
      } else if (virtualCard.screening?.status === ScreeningStatus.Fail) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.Fail}`,
            state: {},
            hash: '',
          }),
        )
      } else if (
        virtualCard.screening?.status === ScreeningStatus.ServiceUnavailable
      ) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.ServiceUnavailable}`,
            state: {},
            hash: '',
          }),
        )
      }
    }
  }, [virtualCard?.screeningRequired, virtualCard?.screening?.status])

  const descriptor = `${sender?.firstNameOrEmail}'s Virtual Card`

  if (isVirtualCardLoading || isSenderLoading || isPermissionsLoading)
    return <LoadingBackgroundPage />
  if (
    userId === undefined ||
    virtualCardPermissions === undefined ||
    virtualCard === undefined ||
    sender === undefined
  )
    return <NotFoundBackgroundPage />
  return (
    <BackgroundPage>
      {!virtualCard.isBillPay && (
        <Rdrct to={billPayDetailUrl(virtualCard.id)} />
      )}
      <Helmet>
        <title>{`${descriptor} | ${appName}`}</title>
        <meta name="description" content={`${descriptor} page of ${appName}`} />
      </Helmet>
      <VirtualCardDetailTopnav
        virtualCard={virtualCard}
        virtualCardPermissions={virtualCardPermissions}
        // cardholder is always shown as 'recipient' for bill pay cards
        recipient={sender}
        recipientEmail={virtualCard.billPayVendorEmail}
      />
      <VirtualCardDetailHeader
        virtualCard={virtualCard}
        virtualCardPermissions={virtualCardPermissions}
        recipient={sender}
        hideCardDetails={hideCardDetails}
        pendingFinalApproval={isPendingFinalApproval}
        hideCardholderName
      />
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent>
          {virtualCard.pending !== undefined && (
            <PendingModificationBox
              virtualCard={virtualCard}
              virtualCardPermissions={virtualCardPermissions}
              timezone={virtualCard.timezone}
              pendingRevision={virtualCard.pending}
            />
          )}
          <HorizontalContentCollection>
            <VerticalContentCollection
              css={css`
                z-index: 1;
              `}
            >
              {virtualCard.recurrence !== undefined && (
                <RecurringBox
                  isInactive={virtualCard.isInactive}
                  timezone={virtualCard.timezone}
                  recurrence={virtualCard.recurrence}
                  openLeftMdUp={false}
                  openLeftSmDown
                  currency={virtualCard.currency}
                />
              )}
              <AdditionalVirtualCardDetailsBox
                virtualCard={virtualCard}
                virtualCardPermissions={virtualCardPermissions}
                sender={sender}
                recipient={sender}
                creditCard={creditCard}
                pendingFinalApproval={isPendingFinalApproval}
              />
              <ActiveUntilBox
                inactiveSinceString={virtualCard.inactiveSinceTimezonedString}
                activeUntilString={virtualCard.activeUntilTimezonedString}
                pendingRecurringTo={
                  virtualCard.isPending && virtualCard.recurs
                    ? updateVirtualCardUrl(virtualCard.id)
                    : undefined
                }
                openLeftMdUp={false}
                openLeftSmDown
                consumedOrClosed={
                  virtualCard.isConsumed || virtualCard.isClosed
                }
              />
              {virtualCard.isPending && (
                <VirtualCardAccountingBox
                  virtualCard={virtualCard}
                  openLeftMdUp={false}
                  openLeftSmDown
                  hasIntegrations={hasIntegrations}
                />
              )}
            </VerticalContentCollection>
            <VerticalContentCollection>
              {!virtualCard.isPending && (
                <VirtualCardAccountingBox
                  virtualCard={virtualCard}
                  openLeftMdUp
                  openLeftSmDown
                  hasIntegrations={hasIntegrations}
                />
              )}
              <NotesBox
                notes={virtualCard.notes}
                copy={translate(
                  "When you create or update a card, leave a memo about what it's for and your note will appear here.",
                )}
              />
            </VerticalContentCollection>
          </HorizontalContentCollection>
          {!virtualCard.isPending && (
            <ActivityBox
              defaultFiltersBody={virtualCardTransactionFilters}
              vcClearedTransactionAmount={
                vcClearedTransactionAmountFromState !== undefined
                  ? vcClearedTransactionAmountFromState[virtualCardId]
                  : undefined
              }
              showTransactionUser={false}
              userId={userId}
              timezone={virtualCard.timezone}
              currency={virtualCard.currency}
              virtualCard={virtualCard}
              clientHeight={clientHeight || 0}
              hideHistory={!showBillPayHistory}
            />
          )}
        </BackgroundPageContent>
      </div>
    </BackgroundPage>
  )
}

export default BillPayDetail
